import * as React from "react";
import type {
  BlockCategory,
  BlockSelection,
  TextCustomizations,
} from "src/types";
import { BlockVariant } from "src/types";

import Button from "@/components/editor/primitives/button/button-component";
import Image from "@/components/editor/primitives/image/image-component";
import RichText from "@/components/editor/primitives/rich-text";
import {
  DEFAULT_BRONZE_SPONSORS,
  DEFAULT_GOLDEN_SPONSORS,
  DEFAULT_HEADING_TEXT,
  DEFAULT_SCHEDULES,
  DEFAULT_SILVER_SPONSORS,
  DEFAULT_SUBHEADING_TEXT,
  DEFAULT_VENDORS,
} from "@/components/editor/shared/constants";
import iframePreview from "@/components/editor/shared/media/iframe/assets/preview.jpg";
import TopLevelIFrame from "@/components/editor/shared/media/iframe/iframe-component";
import VideoGridPreview from "@/components/editor/shared/media/video-grid/assets/preview.png";
import VideoGrid from "@/components/editor/shared/media/video-grid/video-grid-component";
import { VIDEO_AREA_SIZE_SMALL_TYPE } from "@/components/editor/shared/media/video-with-title-settings";
import {
  DEFAULT_NAVIGATION_LINKS,
  getCurrentSelectionLinks,
} from "@/components/editor/shared/navigation";
import embeddedSchedulePreview from "@/components/editor/shared/schedules/embedded-schedule/assets/preview.jpg";
import EmbeddedSchedule from "@/components/editor/shared/schedules/embedded-schedule/embedded-schedule-component";
import Benefits from "@/components/editor/shared/sections/benefits/benefits-component";
// import { DEFAULT_BENEFIT_CARDS } from "@/components/editor/themes/starter-hub/blocks/sections/benefits/constants";
import { DEFAULT_BENEFIT_CARDS } from "@/components/editor/shared/sections/benefits/constants";
import brandMaxiCoverPreview from "@/components/editor/themes/starter-hub/blocks/covers/brand-maxi-cover/block-preview.jpg";
import BrandMaxiCover from "@/components/editor/themes/starter-hub/blocks/covers/brand-maxi-cover/brand-maxi-cover-component";
import { DEFAULT_ABOUT_DETAILS } from "@/components/editor/themes/starter-hub/blocks/covers/constants";
import headerOption2Preview from "@/components/editor/themes/starter-hub/blocks/covers/header-option-2/block-preview.jpg";
// import headerOption2Background from "@/components/editor/themes/starter-hub/blocks/covers/header-option-2/default-background.jpg";
import HeaderOption2 from "@/components/editor/themes/starter-hub/blocks/covers/header-option-2/header-option-2-component";
// import headerOption3Background from "@/components/editor/themes/starter-hub/blocks/covers/header-option-3/default-background.jpg";
import HeaderOption3 from "@/components/editor/themes/starter-hub/blocks/covers/header-option-3/header-option-3-component";
import headerOption3Preview from "@/components/editor/themes/starter-hub/blocks/covers/header-option-3/preview.jpg";
import standaloneNavigationPreview from "@/components/editor/themes/starter-hub/blocks/covers/standalone-navigation/assets/preview.jpeg";
import StandaloneNavigation from "@/components/editor/themes/starter-hub/blocks/covers/standalone-navigation/standalone-navigation-component";
// import vrHeaderLogo from "@/components/editor/themes/starter-hub/blocks/covers/vr-header/logo.png";
import vrHeaderPreview from "@/components/editor/themes/starter-hub/blocks/covers/vr-header/preview.jpeg";
// import vrHeaderPreviewBackground from "@/components/editor/themes/starter-hub/blocks/covers/vr-header/vr-header-background.jpg";
import VRHeader from "@/components/editor/themes/starter-hub/blocks/covers/vr-header/vr-header-component";
import Booths from "@/components/editor/themes/starter-hub/blocks/expo/booths/booths-component";
import SyncedBooths from "@/components/editor/themes/starter-hub/blocks/expo/booths/synced-booths-component";
import boothsPreview from "@/components/editor/themes/starter-hub/blocks/expo/preview.jpg";
import VideoWithParagraphPreview from "@/components/editor/themes/starter-hub/blocks/media/video-with-paragraph/preview.jpg";
import VideoWithParagraph from "@/components/editor/themes/starter-hub/blocks/media/video-with-paragraph/video-with-paragraph-component";
import VideoWithTitle from "@/components/editor/themes/starter-hub/blocks/media/video-with-title/video-with-title-component";
import videoWithTitlePreview from "@/components/editor/themes/starter-hub/blocks/media/video-with-title/video-with-title-preview.jpg";
import defaultSchedulePreview from "@/components/editor/themes/starter-hub/blocks/schedules/assets/preview.jpg";
import DefaultSchedule from "@/components/editor/themes/starter-hub/blocks/schedules/default-schedule/default-schedule-component";
import SyncedDefaultSchedule from "@/components/editor/themes/starter-hub/blocks/schedules/default-schedule/synced-default-schedule-component";
import AboutDetails from "@/components/editor/themes/starter-hub/blocks/sections/about-details/about-details-component";
import aboutDetailsPreview from "@/components/editor/themes/starter-hub/blocks/sections/about-details/preview.jpg";
import AboutSummary from "@/components/editor/themes/starter-hub/blocks/sections/about-summary/about-summary-component";
import aboutSummaryPreview from "@/components/editor/themes/starter-hub/blocks/sections/about-summary/preview.jpg";
import benefitsPreview from "@/components/editor/themes/starter-hub/blocks/sections/benefits/assets/preview.jpg";
import BenefitCard from "@/components/editor/themes/starter-hub/blocks/sections/benefits/benefit-card/benefit-card-component";
import Divider from "@/components/editor/themes/starter-hub/blocks/sections/divider/divider-component";
import dividerPreview from "@/components/editor/themes/starter-hub/blocks/sections/divider/preview.jpg";
import Gallery from "@/components/editor/themes/starter-hub/blocks/sections/gallery";
import galleryPreview from "@/components/editor/themes/starter-hub/blocks/sections/gallery/assets/preview.jpg";
import ParagraphPicture from "@/components/editor/themes/starter-hub/blocks/sections/paragraph-picture/paragraph-picture-component";
// import paragraphPicturePlaceholder from "@/components/editor/themes/starter-hub/blocks/sections/paragraph-picture/paragraph-picture-placeholder.jpg";
import paragraphPicturePreview from "@/components/editor/themes/starter-hub/blocks/sections/paragraph-picture/preview.jpg";
import paragraphPictureCTAPreview from "@/components/editor/themes/starter-hub/blocks/sections/paragraph-picture/previewCTA.jpg";
import RichTextSection from "@/components/editor/themes/starter-hub/blocks/sections/rich-text-section/rich-text-section-component";
import richTextPreview from "@/components/editor/themes/starter-hub/blocks/sections/rich-text-section/rich-text-section-preview.jpg";
import { DEFAULT_SPEAKERS } from "@/components/editor/themes/starter-hub/blocks/speakers/constants";
import speakersPreview from "@/components/editor/themes/starter-hub/blocks/speakers/speakers-block.jpg";
import Speakers from "@/components/editor/themes/starter-hub/blocks/speakers/speakers-component";
import SyncedSpeakers from "@/components/editor/themes/starter-hub/blocks/speakers/synced-speakers-component";
import sponsorsBronzePreview from "@/components/editor/themes/starter-hub/blocks/sponsors/preview-bronze.jpg";
import sponsorsGoldenPreview from "@/components/editor/themes/starter-hub/blocks/sponsors/preview-golden.jpg";
import sponsorsSilverPreview from "@/components/editor/themes/starter-hub/blocks/sponsors/preview-silver.jpg";
import Sponsors from "@/components/editor/themes/starter-hub/blocks/sponsors/sponsors-component";
import SyncedSponsors from "@/components/editor/themes/starter-hub/blocks/sponsors/synced-sponsors-component";
import { sponsorWidgetList } from "@/components/editor/themes/utils/sponsor-widget-list";
import { IMAGE_UPLOAD_TYPES } from "@/features/analytics";
import humanizeDateRange from "@/utils/dates/humanizeDateRange";
import getInitialItemsOrDefault from "@/utils/get-initial-items-or-default";
import getTopLevelNodeTrees from "@/utils/get-top-level-node-trees";

import relateEventsPreview from "../../shared/sections/related-events/assets/preview.jpg";
import type { ThemeTemplateOptions } from "../types";
import RelatedEventsSection from "./blocks/sections/related-events/";

const HEADER_SELECTIONS = {
  header: [
    {
      displayName: "selection-menu.selection.blocks.standalone-navigation",
      previewImage: standaloneNavigationPreview,
      Component: ({ query }) => (
        <StandaloneNavigation>
          {getInitialItemsOrDefault(
            getCurrentSelectionLinks(getTopLevelNodeTrees(query)),
            10,
            DEFAULT_NAVIGATION_LINKS
          ).map(({ label, action }) => (
            <Button
              isLink
              key={label}
              text={label}
              action={action}
              noCustomization
            />
          ))}
        </StandaloneNavigation>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.header-1",
      previewImage: brandMaxiCoverPreview,
      Component: (options: ThemeTemplateOptions) => {
        const {
          event: { name, time_start, time_end, standard_timezone },
        } = options;

        return (
          <BrandMaxiCover
            heading={name || DEFAULT_HEADING_TEXT}
            subheading={DEFAULT_SUBHEADING_TEXT}
            ctaText={humanizeDateRange(
              time_start,
              time_end,
              standard_timezone
            ).join(" - ")}
            backgroundImage="/canvas/seldon/images/default-background-2.65408416.jpg"
            backgroundPosition="center center"
            isLogoShowing
            logoSize={50}
            imageFit="cover"
          />
        );
      },
    },
    {
      displayName: "selection-menu.selection.blocks.header-2",
      previewImage: headerOption2Preview,
      Component: (options: ThemeTemplateOptions) => {
        const {
          event: { name, time_start, time_end, standard_timezone },
        } = options;

        return (
          <HeaderOption2
            backgroundImage="/canvas/seldon/images/default-background.36c0b0a0.jpg"
            backgroundPosition="center center"
            ctaText={humanizeDateRange(
              time_start,
              time_end,
              standard_timezone
            ).join(" - ")}
            heading={name || DEFAULT_HEADING_TEXT}
            isLogoShowing
            subheading={"One week of high energy events, online and in person"}
            logoSize={50}
            imageFit="cover"
          />
        );
      },
    },
    {
      displayName: "selection-menu.selection.blocks.header-3",
      previewImage: headerOption3Preview,
      Component: (options: ThemeTemplateOptions) => {
        const {
          event: { name, time_start, time_end, standard_timezone },
        } = options;

        return (
          <HeaderOption3
            isLogoShowing
            heading={name || DEFAULT_HEADING_TEXT}
            subheading={"One week of high energy events, online and in person"}
            ctaText={humanizeDateRange(
              time_start,
              time_end,
              standard_timezone
            ).join(" - ")}
            backgroundImage="/canvas/seldon/images/default-background.022c751b.jpg"
            backgroundPosition="center center"
            logoSize={50}
            imageFit="cover"
          />
        );
      },
    },
    {
      displayName: "selection-menu.selection.blocks.header-4",
      previewImage: vrHeaderPreview,
      Component: (options: ThemeTemplateOptions) => {
        const {
          event: { time_start, time_end, standard_timezone },
        } = options;

        return (
          <VRHeader
            backgroundImage="/canvas/seldon/images/vr-header-background.3733a07b.jpg"
            backgroundTint="rgba(0, 0, 0, .66)"
            backgroundPosition="center center"
            logo="/canvas/seldon/images/logo.a6af4fa5.png"
            isLogoShowing
            initialHeading={options.event.name || DEFAULT_HEADING_TEXT}
            initialSubheading={
              options.event.description ||
              "One week of high energy events, online and in person"
            }
            initialCtaText={humanizeDateRange(
              time_start,
              time_end,
              standard_timezone
            )}
            logoSize={50}
            imageFit="cover"
          />
        );
      },
    },
  ],
};

const SPONSORS_SELECTIONS = {
  sponsors: [
    {
      displayName:
        "selection-menu.selection.blocks.synced-sponsors-custom-tier",
      stringMap: (tc: TextCustomizations) => ({
        tier: tc?.custom_text?.sponsors?.gold || "Golden",
      }),
      previewImage: sponsorsGoldenPreview,
      variant: BlockVariant.SYNCED,
      Component: () => (
        <SyncedSponsors title="Golden Sponsors" layout="golden" />
      ),
    },
    {
      displayName:
        "selection-menu.selection.blocks.synced-sponsors-custom-tier",
      stringMap: (tc: TextCustomizations) => ({
        tier: tc?.custom_text?.sponsors?.silver || "Silver",
      }),
      previewImage: sponsorsSilverPreview,
      variant: BlockVariant.SYNCED,
      Component: () => (
        <SyncedSponsors title="Silver Sponsors" layout="silver" />
      ),
    },
    {
      displayName:
        "selection-menu.selection.blocks.synced-sponsors-custom-tier",
      stringMap: (tc: TextCustomizations) => ({
        tier: tc?.custom_text?.sponsors?.bronze || "Bronze",
      }),
      previewImage: sponsorsBronzePreview,
      variant: BlockVariant.SYNCED,
      Component: () => (
        <SyncedSponsors title="Bronze Sponsors" layout="bronze" />
      ),
    },
    ...sponsorWidgetList(SyncedSponsors),
    {
      displayName: "selection-menu.selection.blocks.synced-sponsors-no-tier",
      previewImage: sponsorsBronzePreview,
      variant: BlockVariant.SYNCED_EXT,
      Component: () => <SyncedSponsors title="Sponsors" layout="no_tier" />,
    },
    {
      displayName: "selection-menu.selection.blocks.sponsors-golden",
      previewImage: sponsorsGoldenPreview,
      variant: BlockVariant.MANUAL,
      Component: (options: ThemeTemplateOptions) => (
        <Sponsors
          sponsors={getInitialItemsOrDefault(
            options.sponsors,
            3,
            DEFAULT_GOLDEN_SPONSORS
          )}
          title="Golden Sponsors"
          layout="golden"
        />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.sponsors-silver",
      previewImage: sponsorsSilverPreview,
      variant: BlockVariant.MANUAL,
      Component: (options: ThemeTemplateOptions) => (
        <Sponsors
          sponsors={getInitialItemsOrDefault(
            options.sponsors,
            4,
            DEFAULT_SILVER_SPONSORS
          )}
          title="Silver Sponsors"
          layout="silver"
        />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.sponsors-bronze",
      previewImage: sponsorsBronzePreview,
      variant: BlockVariant.MANUAL,
      Component: (options: ThemeTemplateOptions) => (
        <Sponsors
          sponsors={getInitialItemsOrDefault(
            options.sponsors,
            6,
            DEFAULT_BRONZE_SPONSORS
          )}
          title="Bronze Sponsors"
          layout="bronze"
        />
      ),
    },
  ] as BlockSelection[],
};

const SPEAKERS_SELECTIONS = {
  speakers: [
    {
      displayName: "selection-menu.selection.blocks.synced-speakers-grid",
      previewImage: speakersPreview,
      variant: BlockVariant.SYNCED,
      Component: () => <SyncedSpeakers layout="large-cards" />,
    },
    {
      displayName: "selection-menu.selection.blocks.speakers-grid",
      previewImage: speakersPreview,
      variant: BlockVariant.MANUAL,
      Component: (options: ThemeTemplateOptions) => (
        <Speakers
          speakers={getInitialItemsOrDefault(
            options.speakers,
            8,
            DEFAULT_SPEAKERS
          )}
          layout="large-cards"
        />
      ),
    },
  ] as BlockSelection[],
};

const EXPO_SELECTIONS = {
  expo: [
    {
      displayName: "selection-menu.selection.blocks.synced-booths-grid",
      previewImage: boothsPreview,
      variant: BlockVariant.SYNCED,
      Component: () => {
        return <SyncedBooths />;
      },
    },
    {
      displayName: "selection-menu.selection.blocks.booths-grid",
      previewImage: boothsPreview,
      variant: BlockVariant.MANUAL,
      Component: (options: ThemeTemplateOptions) => {
        const vendors = getInitialItemsOrDefault(
          options.vendors,
          6,
          DEFAULT_VENDORS
        );
        return <Booths vendors={vendors} />;
      },
    },
  ] as BlockSelection[],
};

const SCHEDULE_SELECTIONS = {
  schedule: [
    {
      displayName: "selection-menu.selection.blocks.synced-schedule-highlights",
      previewImage: defaultSchedulePreview,
      variant: BlockVariant.SYNCED,
      Component: () => <SyncedDefaultSchedule title="Schedule highlights" />,
    },
    {
      displayName: "selection-menu.selection.blocks.schedule-highlights",
      previewImage: defaultSchedulePreview,
      variant: BlockVariant.MANUAL,
      Component: (options: ThemeTemplateOptions) => (
        <DefaultSchedule
          title="Schedule highlights"
          schedules={
            options.schedules.length > 0 ? options.schedules : DEFAULT_SCHEDULES
          }
        />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.embedded-schedule",
      previewImage: embeddedSchedulePreview,
      deprecated: true,
      Component: (options) => <EmbeddedSchedule slug={options.event.slug} />,
      selectable: () => false,
    },
  ] as BlockSelection[],
};

const SECTIONS_SELECTIONS = {
  sections: [
    {
      displayName: "selection-menu.selection.blocks.rich-text",
      previewImage: richTextPreview,
      Component: () => (
        <RichTextSection text="<h3 style='text-align:center;'>Rich Text Section</h3><p>Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letraset sheets containing Lorem Ipsum passages, and more recently with desktop publishing software like Aldus PageMaker including versions of Lorem Ipsum.</p><p>Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letraset sheets containing Lorem Ipsum passages, and more recently with desktop publishing software like Aldus PageMaker including versions of Lorem Ipsum.</p><p>Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letraset sheets containing Lorem Ipsum passages, and more recently with desktop publishing software like Aldus PageMaker including versions of Lorem Ipsum.</p>" />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.related-events",
      previewImage: relateEventsPreview,
      Component: () => <RelatedEventsSection />,
      selectable: ({ flags, eventSeries }) =>
        flags.eventSeries &&
        Array.isArray(eventSeries) &&
        eventSeries.length > 0,
      previewImageContainerStyle: {
        height: "124px",
        backgroundColor: "#F8FAF4",
        paddingTop: "32px",
      },
    },
    {
      displayName: "selection-menu.selection.blocks.about-2-columns",
      previewImage: aboutSummaryPreview,
      Component: () => (
        <AboutSummary
          title="About"
          paragraphHeading="Interact from home"
          paragraph="Esse delectus corrupti harum consequatur architecto dolore harum dolores. Ex et perspiciatis molestias maxime atque veniam non. Perferendis nisi tempora eum in aperiam dignissimos nam reiciendis. Maxime asperiores aut."
        />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.about-3-columns",
      previewImage: aboutDetailsPreview,
      Component: () => <AboutDetails defaultDetails={DEFAULT_ABOUT_DETAILS} />,
    },
    {
      displayName: "selection-menu.selection.blocks.paragraph-picture",
      previewImage: paragraphPicturePreview,
      Component: () => (
        <ParagraphPicture
          paragraphPictures={[
            {
              subTitle: "<h4>Three days of interviews</h4>",
              description:
                "<p>Over three days of unscripted interviews and interactive workshops, attendees with an eye on emerging trends will hear from and collaborate with entrepreneurs, artists, athletes, scientists and thinkers for a first look at the seismic shifts occurring in the way we live, work and play.</p>",
            },
            {
              imageUrl:
                "/canvas/seldon/images/paragraph-picture-placeholder.6361125c.jpg",
              shape: "circle",
              blockType: IMAGE_UPLOAD_TYPES.PARAGRAPH_AND_PICTURE,
            },
          ]}
        />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.paragraph-picture-cta",
      previewImage: paragraphPictureCTAPreview,
      Component: () => (
        <ParagraphPicture
          paragraphPictures={[
            {
              subTitle: "<h4>Three days of interviews</h4>",
              description:
                "<p>Over three days of unscripted interviews and interactive workshops, attendees with an eye on emerging trends will hear from and collaborate with entrepreneurs, artists, athletes, scientists and thinkers for a first look at the seismic shifts occurring in the way we live, work and play.</p>",
              showCTA: true,
            },
            {
              imageUrl:
                "/canvas/seldon/images/paragraph-picture-placeholder.6361125c.jpg",
              shape: "circle",
              blockType: IMAGE_UPLOAD_TYPES.PARAGRAPH_AND_PICTURE,
            },
          ]}
        />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.divider-white",
      previewImage: dividerPreview,
      Component: () => (
        <Divider
          title="About The Festival"
          paragraph="Over three days of unscripted interviews and interactive workshops, attendees with an eye on emerging trends will hear from and collaborate with entrepreneurs, artists, athletes, scientists and thinkers for a first look at the seismic shifts occurring in the way we live, work and play."
        />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.benefits",
      previewImage: benefitsPreview,
      Component: () => (
        <Benefits>
          {DEFAULT_BENEFIT_CARDS.map((item, i) => (
            <BenefitCard key={i}>
              <Image src={item.icon} />
              <RichText
                text={`<h4 style='text-align:center;'>${item.title}</h4>`}
              />
              <RichText
                text={`<p style='text-align:center;'>${item.description}</p>`}
              />
            </BenefitCard>
          ))}
        </Benefits>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.gallery",
      previewImage: galleryPreview,
      Component: () => (
        <Gallery>
          <Image src="/canvas/shared/images/sample-1.971c7ea0.jpg" />
          <Image src="/canvas/shared/images/sample-2.f08a4422.jpg" />
          <Image src="/canvas/shared/images/sample-3.4061fb7d.jpg" />
          <Image src="/canvas/shared/images/sample-4.7365ade5.jpg" />
          <Image src="/canvas/shared/images/sample-5.32bfda46.jpg" />
        </Gallery>
      ),
    },
  ],
};

const MEDIA_SELECTIONS = {
  media: [
    {
      displayName: "selection-menu.selection.blocks.video-paragraph",
      previewImage: VideoWithParagraphPreview,
      Component: () => <VideoWithParagraph />,
    },
    {
      displayName: "selection-menu.selection.blocks.video-title",
      previewImage: videoWithTitlePreview,
      Component: () => (
        <VideoWithTitle videoAreaSize={VIDEO_AREA_SIZE_SMALL_TYPE} />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.video-grid",
      previewImage: VideoGridPreview,
      Component: () => <VideoGrid />,
    },
    {
      displayName: "selection-menu.selection.blocks.iframe",
      previewImage: iframePreview,
      Component: () => <TopLevelIFrame />,
    },
  ],
};

const seldonHubSelections: Record<BlockCategory, BlockSelection[]> = {
  ...HEADER_SELECTIONS,
  ...SECTIONS_SELECTIONS,
  ...SPONSORS_SELECTIONS,
  ...SPEAKERS_SELECTIONS,
  ...EXPO_SELECTIONS,
  ...SCHEDULE_SELECTIONS,
  ...MEDIA_SELECTIONS,
};

export default seldonHubSelections;
